import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Login.css';
import './EsqueceuSenha.css';
import BASE_URL from '../utils/api';

function EsqueceuSenha() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1: digitar email/login, 2: validar código, 3: nova senha
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    const [login, setLogin] = useState('');
    const [tipoBusca, setTipoBusca] = useState('login'); // 'login' ou 'email'
    const [codigoDigitado, setCodigoDigitado] = useState('');
    const [novaSenha, setNovaSenha] = useState('');
    const [confirmarSenha, setConfirmarSenha] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [reenvioDisabled, setReenvioDisabled] = useState(false);
    const [jaEnviou, setJaEnviou] = useState(false);
    const countdownRef = useRef(null);

    const COOLDOWN_SEGUNDOS = 120;

    const BASE_URLS = [BASE_URL];

    // Countdown timer para reenvio de código
    useEffect(() => {
        if (countdown > 0) {
            countdownRef.current = setTimeout(() => setCountdown(c => c - 1), 1000);
        } else {
            setReenvioDisabled(false);
        }
        return () => clearTimeout(countdownRef.current);
    }, [countdown]);

    // Mascarar email: R*********12@gmail.com
    const mascararEmail = (email) => {
        if (!email) return '***@***.com';
        const [local, dominio] = email.split('@');
        if (local.length <= 3) {
            return local[0] + '***@' + dominio;
        }
        const primeiro = local[0];
        const ultimos = local.slice(-2);
        const asteriscos = '*'.repeat(Math.min(local.length - 3, 9));
        return `${primeiro}${asteriscos}${ultimos}@${dominio}`;
    };

    // Mascarar telefone: mostrar apenas últimos 4 dígitos
    const mascararTelefone = (telefone) => {
        if (!telefone) return '****-****';
        const numeros = telefone.replace(/\D/g, '');
        if (numeros.length < 4) return '****-' + numeros;
        return '****-' + numeros.slice(-4);
    };

    // Helper: tenta múltiplas URLs de base
    const apiFetch = async (endpoint, options = {}) => {
        let lastError;
        for (const base of BASE_URLS) {
            try {
                const resp = await fetch(`${base}${endpoint}`, options);
                return resp;
            } catch (err) {
                lastError = err;
                continue;
            }
        }
        throw lastError;
    };

    // Step 1: Enviar login OU email para RecuperacaoSenhaTenant/solicitar (banco central)
    const handleSolicitarCodigo = async (e) => {
        e.preventDefault();
        setError('');

        if (!login.trim()) {
            setError(tipoBusca === 'email' ? 'Digite seu e-mail.' : 'Digite seu login.');
            return;
        }

        setLoading(true);
        try {
            const body = tipoBusca === 'email'
                ? { Email: login.trim() }
                : { Login: login.trim() };

            const resp = await apiFetch('/RecuperacaoSenhaTenant/solicitar', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            const data = await resp.json().catch(() => ({}));

            if (!resp.ok) {
                throw new Error(data.message || 'Erro ao solicitar código.');
            }

            // Backend retorna o loginResolvido (necessário para validar-codigo e atualizar-senha)
            if (data.loginResolvido) {
                setLogin(data.loginResolvido);
                setTipoBusca('login'); // próximos steps sempre usam login
            }

            setJaEnviou(true);
            setSuccess(data.message || 'Código enviado para seu e-mail!');
            setCountdown(COOLDOWN_SEGUNDOS);
            setReenvioDisabled(true);
            setTimeout(() => {
                setSuccess('');
                setStep(2);
            }, 1500);
        } catch (err) {
            setError(err.message || 'Erro de conexão com o servidor.');
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Validar código no backend antes de avançar
    const handleValidarCodigo = async (e) => {
        e.preventDefault();
        setError('');

        if (!codigoDigitado.trim() || codigoDigitado.length !== 6) {
            setError('Digite o código de 6 dígitos.');
            return;
        }

        setLoading(true);
        try {
            const resp = await apiFetch('/RecuperacaoSenhaTenant/validar-codigo', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Login: login.trim(), Codigo: codigoDigitado })
            });

            if (!resp.ok) {
                setError('Erro ao verificar código. Tente novamente.');
                setLoading(false);
                return;
            }

            const data = await resp.json();

            if (!data.valido) {
                setError(data.message || 'Código inválido ou expirado.');
                setLoading(false);
                return;
            }

            setSuccess('Código verificado com sucesso!');
            setTimeout(() => {
                setSuccess('');
                setStep(3);
            }, 1000);
        } catch (err) {
            setError('Erro de conexão ao verificar código.');
        } finally {
            setLoading(false);
        }
    };

    // Validação de senha
    const passwordRules = (pwd) => {
        const rules = [
            { id: 'length', label: 'Mínimo 6 caracteres', ok: pwd && pwd.length >= 6 },
            { id: 'composition', label: 'Contém letras, números e caractere especial', ok: pwd && /[A-Za-z]/.test(pwd) && /\d/.test(pwd) && /[^A-Za-z0-9]/.test(pwd) },
        ];
        return rules;
    };

    const toggleShow = () => setShowPassword(s => !s);

    // Step 3: Alterar senha (chama backend para validar código + trocar senha)
    const handleAlterarSenha = async (e) => {
        e.preventDefault();
        setError('');

        const rules = passwordRules(novaSenha);
        const validPassword = rules.every(r => r.ok);

        if (!novaSenha || !confirmarSenha) {
            setError('Preencha todos os campos.');
            return;
        }

        if (!validPassword) {
            setError('A senha não atende aos requisitos.');
            return;
        }

        if (novaSenha !== confirmarSenha) {
            setError('A senha e a confirmação devem ser iguais.');
            return;
        }

        setLoading(true);
        try {
            const resp = await apiFetch('/RecuperacaoSenhaTenant/atualizar-senha', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Login: login.trim(),
                    Codigo: codigoDigitado,
                    NovaSenha: novaSenha,
                    ConfirmarNovaSenha: confirmarSenha
                })
            });

            if (!resp.ok) {
                const errorData = await resp.json().catch(() => ({ message: 'Erro ao alterar senha.' }));
                throw new Error(errorData.message || 'Código inválido ou expirado.');
            }

            setSuccess('Senha alterada com sucesso! Redirecionando para o login...');
            setTimeout(() => {
                navigate('/sidebar/Login');
            }, 2500);
        } catch (err) {
            setError(err.message || 'Erro ao alterar senha.');
        } finally {
            setLoading(false);
        }
    };

    // Reenviar código com cooldown
    const handleReenviarCodigo = async () => {
        if (reenvioDisabled) return;
        setError('');
        setReenvioDisabled(true);
        setLoading(true);

        try {
            const resp = await apiFetch('/RecuperacaoSenhaTenant/solicitar', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Login: login.trim() })
            });

            if (!resp.ok) {
                const errorData = await resp.json().catch(() => ({ message: 'Erro ao reenviar código.' }));
                throw new Error(errorData.message || 'Erro ao reenviar código.');
            }

            setJaEnviou(true);
            setSuccess('Novo código enviado para seu e-mail!');
            setCodigoDigitado('');
            setCountdown(COOLDOWN_SEGUNDOS);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message || 'Erro ao reenviar código.');
            setReenvioDisabled(false);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <h2>🔑 Recuperar Senha</h2>
                    <p>
                        {step === 1 && 'Digite seu e-mail para continuar'}
                        {step === 2 && 'Digite o código de verificação'}
                        {step === 3 && 'Crie uma nova senha'}
                    </p>
                </div>

                {/* STEP 1: Digitar Email/Login e enviar código */}
                {step === 1 && (
                    <form onSubmit={handleSolicitarCodigo} className="login-form">
                        {error && <div className="error-message">⚠️ {error}</div>}
                        {success && <div className="success-message">✓ {success}</div>}

                        <div className="form-group">
                            <label htmlFor="tipoBusca">Recuperar por</label>
                            <select
                                id="tipoBusca"
                                value={tipoBusca}
                                onChange={(e) => { setTipoBusca(e.target.value); setLogin(''); setError(''); }}
                                disabled={loading}
                                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', marginBottom: '4px' }}
                            >
                                <option value="login">Login</option>
                                <option value="email">E-mail</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="login">{tipoBusca === 'email' ? 'E-mail' : 'Login'}</label>
                            <input
                                id="login"
                                name="login"
                                type={tipoBusca === 'email' ? 'email' : 'text'}
                                value={login}
                                onChange={(e) => setLogin(e.target.value)}
                                placeholder={tipoBusca === 'email' ? 'Digite seu e-mail' : 'Digite seu login'}
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className="login-actions">
                            <button type="submit" className="btn-continuar" disabled={loading}>
                                {loading ? '⏳ Enviando...' : 'Enviar Código'}
                            </button>
                        </div>

                        <div className="criar-conta-text" style={{ marginTop: '16px' }}>
                            <span onClick={() => navigate('/sidebar/Login')} className="criar-conta-link" style={{ cursor: 'pointer' }}>
                                ← Voltar ao Login
                            </span>
                        </div>
                    </form>
                )}

                {/* STEP 2: Validar Código */}
                {step === 2 && (
                    <form onSubmit={handleValidarCodigo} className="login-form">
                        {error && <div className="error-message">⚠️ {error}</div>}
                        {success && <div className="success-message">✓ {success}</div>}

                        <p className="codigo-instrucao">
                            Enviamos um código de 6 dígitos para o e-mail:
                            <br />
                            <strong>{mascararEmail(login)}</strong>
                        </p>

                        <div className="form-group">
                            <label htmlFor="codigo">Código de Verificação</label>
                            <input 
                                id="codigo" 
                                name="codigo" 
                                value={codigoDigitado} 
                                onChange={(e) => setCodigoDigitado(e.target.value.replace(/\D/g, ''))} 
                                placeholder="000000"
                                maxLength="6"
                                required 
                                disabled={loading}
                                style={{ textAlign: 'center', fontSize: '24px', letterSpacing: '8px' }}
                            />
                        </div>

                        <div className="login-actions">
                            <button type="submit" className="btn-continuar" disabled={loading || codigoDigitado.length !== 6}>
                                {loading ? '⏳ Validando...' : 'Validar Código'}
                            </button>
                        </div>

                        <div className="criar-conta-text" style={{ marginTop: '16px', textAlign: 'center' }}>
                            {reenvioDisabled && countdown > 0 ? (
                                <span style={{ color: '#999', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ verticalAlign: 'middle' }}>
                                        <circle cx="12" cy="12" r="10" stroke="#999" strokeWidth="1.5"/>
                                        <path d="M12 6v6l4 2" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Reenviar código em {String(Math.floor(countdown / 60)).padStart(2, '0')}:{String(countdown % 60).padStart(2, '0')}
                                </span>
                            ) : (
                                <>Não recebeu?{' '}
                                    <span onClick={handleReenviarCodigo} className="criar-conta-link" style={{ cursor: 'pointer' }}>
                                        {jaEnviou ? 'Reenviar código' : 'Enviar código'}
                                    </span>
                                </>
                            )}
                        </div>

                        <div className="criar-conta-text" style={{ marginTop: '8px' }}>
                            <span onClick={() => setStep(1)} className="criar-conta-link" style={{ cursor: 'pointer' }}>
                                ← Voltar
                            </span>
                        </div>
                    </form>
                )}

                {/* STEP 3: Nova Senha */}
                {step === 3 && (
                    <form onSubmit={handleAlterarSenha} className="login-form">
                        {error && <div className="error-message">⚠️ {error}</div>}
                        {success && <div className="success-message">✓ {success}</div>}

                        <div className="form-group password-group">
                            <label htmlFor="novaSenha">Nova Senha</label>
                            <div className="input-with-icon">
                                <input 
                                    id="novaSenha" 
                                    name="novaSenha" 
                                    type={showPassword ? 'text' : 'password'} 
                                    value={novaSenha} 
                                    onChange={(e) => setNovaSenha(e.target.value.replace(/\s/g, ''))} 
                                    required 
                                    disabled={loading} 
                                />
                                <button type="button" onClick={toggleShow} className="password-toggle" aria-label="Mostrar senha">
                                    {showPassword ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                            <path d="M3 3l18 18" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M17.94 17.94A10.95 10.95 0 0 1 12 20c-5 0-9.27-3-11-8 1.1-2.9 3.2-5.2 5.7-6.6" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M9.88 9.88a3 3 0 0 0 4.24 4.24" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            <circle cx="12" cy="12" r="3" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="form-group password-group">
                            <label htmlFor="confirmarSenha">Confirmar Nova Senha</label>
                            <div className="input-with-icon">
                                <input 
                                    id="confirmarSenha" 
                                    name="confirmarSenha" 
                                    type={showPassword ? 'text' : 'password'} 
                                    value={confirmarSenha} 
                                    onChange={(e) => setConfirmarSenha(e.target.value.replace(/\s/g, ''))} 
                                    required 
                                    disabled={loading} 
                                />
                                <button type="button" onClick={toggleShow} className="password-toggle" aria-label="Mostrar senha">
                                    {showPassword ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                            <path d="M3 3l18 18" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M17.94 17.94A10.95 10.95 0 0 1 12 20c-5 0-9.27-3-11-8 1.1-2.9 3.2-5.2 5.7-6.6" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M9.88 9.88a3 3 0 0 0 4.24 4.24" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            <circle cx="12" cy="12" r="3" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        <ul className="password-rules">
                            {passwordRules(novaSenha).map(rule => (
                                <li key={rule.id} className={`password-rule ${rule.ok ? 'valid' : 'invalid'}`}>
                                    {rule.ok ? '✓' : '●'} {rule.label}
                                </li>
                            ))}
                        </ul>

                        <div className="login-actions">
                            <button 
                                type="submit" 
                                className="btn-continuar" 
                                disabled={loading || !passwordRules(novaSenha).every(r => r.ok) || novaSenha !== confirmarSenha}
                            >
                                {loading ? '⏳ Alterando...' : 'Alterar Senha'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

export default EsqueceuSenha;
